/**
 * ACA App URL Builder
 *
 * Centralized URL construction for Azure Container App workflow runtime APIs.
 * Replaces the scattered buildBaseUrl() / buildRuntimeBaseUrl() functions
 * that previously routed through ARM's /hostruntime/ proxy.
 *
 * ACA apps expose the same runtime APIs as local StandardRuntimeHost,
 * directly on their FQDN — no ARM proxy needed:
 *
 *   Local:  http://localhost:7071/runtime/webhooks/workflow/api/management/...
 *   ACA:    https://{fqdn}/runtime/webhooks/workflow/api/management/...
 *
 * In dev mode (Vite), ACA calls are proxied through /aca-proxy/ to avoid CORS.
 * The Vite dev server rewrites /aca-proxy/{fqdn}/... → https://{fqdn}/...
 *
 * For ARM management operations (app details, keys, identity), use buildAcaArmUrl().
 */

import { isLocalMode, getLocalRuntimeUrl } from './localRuntimeClient';

/** Whether we're running in Vite dev server (needs CORS proxy) */
const isDevMode = (): boolean => import.meta.env.DEV;

/**
 * Build the base URL for direct-plane calls to an ACA app.
 * In dev mode, routes through the Vite proxy to avoid CORS.
 */
const getAcaBaseUrl = (hostName: string): string => {
  if (isDevMode()) {
    // Route through Vite proxy: /aca-proxy/{hostName} → https://{hostName}
    return `/aca-proxy/${hostName}`;
  }
  return `https://${hostName}`;
};

/**
 * Build base URL for runtime management APIs (workflows, runs, operations, etc.)
 *
 * @param hostName - FQDN of the ACA app (e.g., "myapp.orangesea-123.eastus.azurecontainerapps.io")
 * @returns Full base URL for runtime management endpoints
 */
export const buildRuntimeUrl = (hostName: string): string => {
  if (isLocalMode()) {
    return `${getLocalRuntimeUrl()}/runtime/webhooks/workflow/api/management`;
  }
  if (!hostName) {
    console.error('[acaAppUrl] buildRuntimeUrl called with empty hostName — app FQDN not available yet');
  }
  return `${getAcaBaseUrl(hostName)}/runtime/webhooks/workflow/api/management`;
};

/**
 * Build base URL for VFS admin APIs (file read/write)
 *
 * @param hostName - FQDN of the ACA app
 * @returns Full base URL for VFS endpoints
 */
export const buildVfsUrl = (hostName: string): string => {
  if (isLocalMode()) {
    return `${getLocalRuntimeUrl()}/admin/vfs`;
  }
  return `${getAcaBaseUrl(hostName)}/admin/vfs`;
};

/**
 * Build ARM URL for ACA management operations (app details, secrets, auth config, etc.)
 * These are the only calls that still go through ARM — runtime/VFS calls go direct.
 * ARM calls use the bearer token and don't have CORS issues.
 *
 * @returns ARM URL for the Container App resource
 */
export const buildAcaArmUrl = (
  subscriptionId: string,
  resourceGroup: string,
  appName: string,
): string =>
  `https://management.azure.com/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.App/containerApps/${appName}`;

/**
 * Build the full base URL for direct-plane API calls (runtime or VFS).
 * In local mode, returns the local runtime URL.
 * In cloud mode, returns the proxied or direct URL.
 *
 * @param hostName - FQDN of the ACA app
 * @returns Base URL (no trailing path)
 */
export const buildDirectBaseUrl = (hostName: string): string => {
  if (isLocalMode()) {
    return getLocalRuntimeUrl();
  }
  return getAcaBaseUrl(hostName);
};
